'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import WalletModal from '@/components/ui/WalletModal';
import { useWallet } from '@/lib/wallet/WalletContext';
import MobileBottomNav from '@/components/ui/MobileBottomNav';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  tooltip: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Trade', path: '/trading-dashboard', icon: 'ChartBarIcon', tooltip: 'Real-time trading interface' },
  { label: 'Portfolio', path: '/portfolio-management', icon: 'BriefcaseIcon', tooltip: 'Asset management & DeFi' },
  { label: 'Social', path: '/social-trading-hub', icon: 'UsersIcon', tooltip: 'Leaderboards & referrals' },
  { label: 'Bots', path: '/trading-bots', icon: 'CpuChipIcon', tooltip: 'Automated trading bots' },
  { label: 'Predict', path: '/predictions-market', icon: 'LightBulbIcon', tooltip: 'Prediction markets' },
];

const TICKER_PAIRS = [
  { pair: 'BTC/USDT', price: '67,842.50', change: '+2.34%', positive: true },
  { pair: 'ETH/USDT', price: '3,521.18', change: '+1.87%', positive: true },
  { pair: 'SOL/USDT', price: '182.44', change: '-0.92%', positive: false },
  { pair: 'BNB/USDT', price: '412.30', change: '+0.55%', positive: true },
  { pair: 'ADA/USDT', price: '0.6821', change: '-1.23%', positive: false },
  { pair: 'AVAX/USDT', price: '38.74', change: '+3.12%', positive: true },
  { pair: 'DOT/USDT', price: '8.92', change: '-0.44%', positive: false },
  { pair: 'MATIC/USDT', price: '0.9134', change: '+1.05%', positive: true },
];

function PriceTicker() {
  const doubled = [...TICKER_PAIRS, ...TICKER_PAIRS];
  return (
    <div className="border-t border-[rgba(76,110,244,0.12)] bg-[#0B0B0F] overflow-hidden h-8 flex items-center">
      <div className="flex items-center gap-8 ticker-scroll whitespace-nowrap">
        {doubled.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs shrink-0">
            <span className="text-[#94A3B8] font-caption">{item.pair}</span>
            <span className="font-data tabular-nums text-[#F1F5F9]">{item.price}</span>
            <span className={`font-data tabular-nums ${item.positive ? 'text-success' : 'text-error'}`}>{item.change}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const Header = () => {
  const pathname = usePathname();
  const { isConnected, shortAddress, walletType, hyperliquidLinked, disconnect, usdcBalance } = useWallet();
  const [walletDropdownOpen, setWalletDropdownOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [wsStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connected');
  const [sessionMinutes, setSessionMinutes] = useState(47);
  const walletDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (walletDropdownRef.current && !walletDropdownRef.current.contains(e.target as Node)) {
        setWalletDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setSessionMinutes(prev => (prev > 0 ? prev - 1 : 60));
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const wsStatusColor = {
    connected: 'bg-success',
    connecting: 'bg-warning',
    disconnected: 'bg-error',
  }[wsStatus];

  const wsStatusLabel = {
    connected: 'Live',
    connecting: 'Connecting',
    disconnected: 'Offline',
  }[wsStatus];

  const walletTypeLabel = walletType === 'metamask' ? 'MetaMask' : walletType === 'phantom' ? 'Phantom' : walletType === 'ledger' ? 'Ledger' : '';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[200] bg-card border-b border-[rgba(76,110,244,0.2)]" style={{ boxShadow: '0 0 20px rgba(76,110,244,0.1)' }}>
        {/* Main nav row */}
        <div className="flex items-center h-14 md:h-16 px-4 lg:px-6">
          {/* Logo */}
          <Link href="/trading-dashboard" className="flex items-center gap-2 shrink-0 mr-8">
            <Image
              src="/assets/images/Group_19-1773175535080.png"
              alt="NordicX Logo"
              width={120}
              height={36}
              className="h-8 md:h-9 w-auto object-contain"
              priority
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1" aria-label="Primary navigation">
            {NAV_ITEMS.map((item) => {
              const isActive = pathname === item.path || pathname?.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  href={item.path}
                  title={item.tooltip}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-body font-medium transition-all duration-250 ease-trading relative group ${
                    isActive
                      ? 'text-[#4c6ef4] bg-[rgba(76,110,244,0.12)]'
                      : 'text-muted-foreground hover:text-foreground hover:bg-[rgba(76,110,244,0.07)]'
                  }`}
                >
                  <Icon name={item.icon as any} size={16} variant={isActive ? 'solid' : 'outline'} />
                  <span>{item.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[#4c6ef4]" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex-1 md:hidden" />

          {/* Right side controls */}
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            {/* WS Status — hidden on mobile */}
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-[rgba(76,110,244,0.07)] border border-[rgba(76,110,244,0.2)]">
              <span className={`w-1.5 h-1.5 rounded-full ${wsStatusColor} ${wsStatus !== 'disconnected' ? 'ws-pulse' : ''}`} />
              <span className="text-xs font-caption text-muted-foreground">{wsStatusLabel}</span>
            </div>

            {/* Session indicator */}
            <div className="hidden lg:flex items-center gap-1.5 px-2 py-1 bg-[rgba(76,110,244,0.07)] border border-[rgba(76,110,244,0.2)]">
              <Icon name="ClockIcon" size={12} className="text-muted-foreground" />
              <span className="text-xs font-caption text-muted-foreground">{sessionMinutes}m</span>
            </div>

            {/* Wallet connection */}
            <div className="relative" ref={walletDropdownRef}>
              {isConnected ? (
                <>
                  <button
                    onClick={() => setWalletDropdownOpen(!walletDropdownOpen)}
                    className="flex items-center gap-2 px-2.5 md:px-3 py-2 text-sm font-body font-medium transition-all duration-250 ease-trading border bg-[rgba(76,110,244,0.12)] border-[rgba(76,110,244,0.3)] text-[#4c6ef4] hover:bg-[rgba(76,110,244,0.18)]"
                    style={{ boxShadow: '0 0 20px rgba(76,110,244,0.15)', minHeight: '44px' }}
                    aria-expanded={walletDropdownOpen}
                    aria-haspopup="true"
                  >
                    <Icon name="WalletIcon" size={16} variant="solid" />
                    <span className="hidden sm:block font-data tabular-nums">{shortAddress}</span>
                    {usdcBalance !== null && (
                      <span className="hidden md:flex items-center gap-1 text-xs font-mono tabular-nums text-[#22C55E] bg-[rgba(34,197,94,0.1)] px-1.5 py-0.5 border border-[rgba(34,197,94,0.25)]">
                        {usdcBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC
                      </span>
                    )}
                    {hyperliquidLinked && (
                      <span className="hidden lg:flex items-center gap-1 text-xs text-[#22C55E]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                        HL
                      </span>
                    )}
                    <Icon name="ChevronDownIcon" size={14} className={`transition-transform duration-250 ${walletDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {walletDropdownOpen && (
                    <div
                      className="absolute right-0 top-full mt-2 w-64 bg-card border border-[rgba(76,110,244,0.2)] py-2 z-[100]"
                      style={{ boxShadow: '0 12px 24px rgba(0,0,0,0.4), 0 0 20px rgba(76,110,244,0.12)' }}
                    >
                      <div className="px-4 py-3 border-b border-[rgba(76,110,244,0.15)]">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-muted-foreground font-caption">Connected via {walletTypeLabel}</p>
                          {hyperliquidLinked && (
                            <span className="text-[9px] px-1.5 py-0.5 bg-[rgba(34,197,94,0.15)] text-[#22C55E] font-medium">HL Linked</span>
                          )}
                        </div>
                        <p className="text-sm font-data text-foreground tabular-nums">{shortAddress}</p>
                        {usdcBalance !== null && (
                          <div className="flex items-center gap-1.5 mt-2">
                            <span className="text-xs text-muted-foreground font-caption">Balance:</span>
                            <span className="text-sm font-mono tabular-nums text-[#22C55E] font-semibold">
                              {usdcBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="px-2 pt-2">
                        <button
                          onClick={() => { navigator.clipboard?.writeText(shortAddress); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-[rgba(76,110,244,0.07)] transition-all duration-250"
                        >
                          <Icon name="ClipboardIcon" size={14} />
                          Copy Address
                        </button>
                        <button
                          onClick={() => { disconnect(); setWalletDropdownOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-[rgba(239,68,68,0.08)] transition-all duration-250"
                        >
                          <Icon name="ArrowRightOnRectangleIcon" size={14} />
                          Disconnect
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setWalletModalOpen(true)}
                  className="flex items-center gap-2 px-2.5 md:px-3 py-2 text-sm font-body font-medium transition-all duration-250 ease-trading border bg-gradient-to-r from-[#3259f3] to-[#4c6ef4] text-white border-[#3259f3] hover:opacity-90"
                  aria-label="Connect Wallet"
                  style={{ minHeight: '44px' }}
                >
                  <Icon name="WalletIcon" size={16} variant="outline" />
                  <span className="hidden sm:block">Connect Wallet</span>
                  <span className="sm:hidden text-xs">Connect</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Price Ticker — hidden on mobile to save space */}
        <div className="hidden md:block">
          <PriceTicker />
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      <WalletModal isOpen={walletModalOpen} onClose={() => setWalletModalOpen(false)} />
    </>
  );
};

export default Header;